import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pause, Play, Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, cabecalhoEmpresa, del, mensagemDeErro, post } from '../../nucleo/api';
import { campoEmpresaDoRegistro, empresaEscolhida } from '../../nucleo/empresa/empresa-do-registro';
import { data, dinheiro, nomeParceiro, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { opcoesDeCategoria, useCategoriasFin } from './dados';
import { ItensEditor, itensDoModelo, itensParaApi, novoItem, type ItemForm } from './ItensEditor';
import { hojeLocal, invalidarContas, type Recorrencia } from './tipos';

/** M5.3: recorrências mensal, trimestral e anual. O job diário emite o documento de cada ciclo. */
export function Recorrencias() {
  const { t } = useTranslation('contas');
  const s = useSessao();
  const [form, setForm] = useState<Recorrencia | 'novo' | null>(null);

  const colunas: Coluna<Recorrencia>[] = [
    {
      chave: 'descricao',
      rotulo: t('campos.descricao'),
      principal: true,
      render: (r) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium" title={textoLivre(r.descricao, r.parceiro_nome)}>{textoLivre(r.descricao, r.parceiro_nome)}</span>
          <span className="truncate text-xs text-tinta-3" title={nomeParceiro(r.parceiro_nome)}>{nomeParceiro(r.parceiro_nome)}</span>
        </span>
      ),
    },
    { chave: 'tipo', rotulo: t('campos.tipo'), render: (r) => t(`tipo.${r.tipo}`) },
    { chave: 'periodicidade', rotulo: t('recorrencia.periodicidade'), render: (r) => t(`periodicidade.${r.periodicidade}`) },
    { chave: 'valor_centavos', rotulo: t('campos.valor'), ordenavel: true, alinhar: 'direita', mono: true, render: (r) => dinheiro(r.valor_centavos, r.moeda) },
    { chave: 'proxima_data', rotulo: t('recorrencia.proxima'), ordenavel: true, mono: true, render: (r) => (r.ativa ? data(r.proxima_data) : '') },
    { chave: 'documentos', rotulo: t('recorrencia.geradas'), alinhar: 'direita', mono: true, render: (r) => String(r.documentos) },
    { chave: 'ativa', rotulo: t('campos.estado'), render: (r) => <Selo cor={r.ativa ? 'verde' : 'neutro'}>{r.ativa ? t('recorrencia.ativa') : t('recorrencia.pausada')}</Selo> },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <AcaoPrimaria>
        {s.pode('contas.configurar') && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setForm('novo')}>
            {t('acoes.novaRecorrencia')}
          </Botao>
        )}
      </AcaoPrimaria>
      <p className="text-sm text-tinta-3">{t('recorrencia.explicacao')}</p>
      <Tabela<Recorrencia>
        tela="contas.recorrencias"
        caminho="/contas/recorrencias"
        colunas={colunas}
        aoClicar={(r) => setForm(r)}
        vazio={<Vazio texto={t('vazio.recorrencias')} />}
      />
      {form && <FormRecorrencia key={form === 'novo' ? 'novo' : form.id} recorrencia={form === 'novo' ? null : form} aoFechar={() => setForm(null)} />}
    </div>
  );
}

function FormRecorrencia({ recorrencia, aoFechar }: { recorrencia: Recorrencia | null; aoFechar: () => void }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const categorias = useCategoriasFin();
  const [novoId] = useState(() => crypto.randomUUID());
  const [tipo, setTipo] = useState<'fatura' | 'conta_pagar'>(recorrencia?.tipo ?? 'fatura');
  const [itens, setItens] = useState<ItemForm[]>(recorrencia ? itensDoModelo(recorrencia.modelo.itens) : [novoItem()]);
  const [removendo, setRemovendo] = useState(false);
  const editando = !!recorrencia;
  const podeEditar = s.pode('contas.configurar');
  const receber = tipo === 'fatura';

  const campos: DefCampo[] = [
    // V2 verticais: recorrência nova sem empresa na aba (sem filtro, várias permitidas) pede a empresa.
    ...(editando ? [] : campoEmpresaDoRegistro(s.empresas, { rotulo: tn('empresa.rotulo'), ajuda: tn('empresa.ajudaRegistro') })),
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'texto', obrigatorio: true, nomes: [recorrencia?.parceiro_nome] },
    ...(editando
      ? []
      : [
          {
            nome: 'parceiro_id',
            rotulo: receber ? t('campos.cliente') : t('campos.fornecedor'),
            tipo: 'busca' as const,
            obrigatorio: true,
            busca: { caminho: '/cadastros/parceiros', params: { papel: receber ? 'cliente' : 'fornecedor', operacao: true }, rotulo: (i: Record<string, unknown>) => String(i.nome) },
          },
        ]),
    { nome: 'periodicidade', rotulo: t('recorrencia.periodicidade'), tipo: 'selecao', obrigatorio: true, largura: 'terco', opcoes: (['mensal', 'trimestral', 'anual'] as const).map((p) => ({ valor: p, rotulo: t(`periodicidade.${p}`) })) },
    { nome: 'proxima_data', rotulo: t('recorrencia.proximaEmissao'), tipo: 'data', obrigatorio: true, largura: 'terco', ajuda: t('recorrencia.proximaAjuda') },
    { nome: 'prazo_dias', rotulo: t('recorrencia.prazo'), tipo: 'numero', obrigatorio: true, largura: 'terco', min: 0, max: 365, passo: 1, ajuda: t('recorrencia.prazoAjuda') },
    { nome: 'fim', rotulo: t('recorrencia.fim'), tipo: 'data', largura: 'terco', ajuda: t('recorrencia.fimAjuda') },
    { nome: 'juros_mes_pct', rotulo: t('campos.jurosMes'), tipo: 'percentual', largura: 'terco', min: 0, max: 100 },
    { nome: 'multa_pct', rotulo: t('campos.multa'), tipo: 'percentual', largura: 'terco', min: 0, max: 100 },
    { nome: 'descricao_documento', rotulo: t('recorrencia.descricaoDocumento'), tipo: 'texto', nomes: [recorrencia?.parceiro_nome] },
    { nome: 'ativa', rotulo: t('recorrencia.ativa'), tipo: 'booleano' },
  ];
  const inicial = recorrencia
    ? {
        descricao: recorrencia.descricao,
        periodicidade: recorrencia.periodicidade,
        proxima_data: recorrencia.proxima_data,
        prazo_dias: recorrencia.prazo_dias,
        fim: recorrencia.fim ?? '',
        juros_mes_pct: recorrencia.modelo.juros_mes_pct ?? null,
        multa_pct: recorrencia.modelo.multa_pct ?? null,
        descricao_documento: recorrencia.modelo.descricao_documento ?? '',
        ativa: recorrencia.ativa,
      }
    : { periodicidade: 'mensal', proxima_data: hojeLocal(), prazo_dias: 10, ativa: true };

  const remover = useMutation({
    mutationFn: () => del(`/contas/recorrencias/${recorrencia!.id}`),
    onSuccess: async () => {
      await invalidarContas(qc);
      toast.sucesso(tn('comum.removido'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  return (
    <>
      <Drawer
        aberto
        aoFechar={aoFechar}
        largura="lg"
        titulo={editando ? t('recorrencia.editar') : t('acoes.novaRecorrencia')}
        rodape={
          <>
            {editando && s.pode('contas.excluir') && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setRemovendo(true)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            {podeEditar && (
              <>
                <Botao variante="fantasma" onClick={aoFechar}>
                  {tn('comum.cancelar')}
                </Botao>
                <Botao variante="primario" type="submit" form="form-recorrencia">
                  {tn('comum.salvar')}
                </Botao>
              </>
            )}
          </>
        }
      >
        {!editando && (
          <div className="mb-4">
            <Rotulo id="tipo-recorrencia" rotulo={t('campos.tipo')} obrigatorio>
              <Selecao
                id="tipo-recorrencia"
                opcoes={(['fatura', 'conta_pagar'] as const).map((x) => ({ valor: x, rotulo: t(`tipo.${x}`) }))}
                value={tipo}
                onChange={(e) => {
                  setTipo(e.target.value as 'fatura' | 'conta_pagar');
                  setItens((l) => l.map((i) => ({ ...i, categoria_id: '' })));
                }}
              />
            </Rotulo>
          </div>
        )}
        {editando && (
          <p className="mb-4 flex items-center gap-2 text-sm text-tinta-2">
            {recorrencia.ativa ? <Play className="size-4 text-verde" aria-hidden /> : <Pause className="size-4 text-tinta-3" aria-hidden />}
            {t('recorrencia.resumo', { parceiro: nomeParceiro(recorrencia.parceiro_nome), tipo: t(`tipo.${recorrencia.tipo}`), count: recorrencia.documentos })}
          </p>
        )}
        <Formulario
          key={tipo}
          id="form-recorrencia"
          campos={campos}
          inicial={inicial}
          aoEnviar={async (v) => {
            const itensApi = itensParaApi(itens, { semItens: t('erros.semItens'), incompleto: t('erros.itemIncompleto') });
            const comum = {
              descricao: String(v.descricao ?? '').trim(),
              periodicidade: v.periodicidade,
              itens: itensApi,
              proxima_data: v.proxima_data,
              prazo_dias: Number(v.prazo_dias ?? 10),
              fim: v.fim || null,
              juros_mes_pct: (v.juros_mes_pct as number | null) ?? undefined,
              multa_pct: (v.multa_pct as number | null) ?? undefined,
              descricao_documento: String(v.descricao_documento ?? '').trim() || null,
              ativa: !!v.ativa,
            };
            if (recorrencia) await api('PATCH', `/contas/recorrencias/${recorrencia.id}`, comum);
            else await post('/contas/recorrencias', { id: novoId, tipo, parceiro_id: v.parceiro_id, ...comum }, cabecalhoEmpresa(empresaEscolhida(v)));
            await invalidarContas(qc);
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
          }}
        >
          <ItensEditor itens={itens} aoMudar={setItens} categorias={opcoesDeCategoria(categorias.data?.itens ?? [], receber)} moeda={recorrencia?.moeda ?? s.workspace.moeda} />
        </Formulario>
      </Drawer>
      <Confirmar aberto={removendo} aoFechar={() => setRemovendo(false)} aoConfirmar={() => remover.mutate()} carregando={remover.isPending} perigoso titulo={t('recorrencia.removerTitulo')} texto={t('recorrencia.removerTexto')} />
    </>
  );
}
